import {
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  InputGroup,
  Row,
} from 'react-bootstrap';
import React from 'react';
import styled from 'styled-components';
import * as CleaveNode from 'cleave.js/dist/cleave-react-node';
import { capitalize } from 'lodash';
import { Field } from 'redux-form';
import { SelectField } from '../../components/ReduxFormField';

import { required } from 'redux-form-validators';
import { ValidationError } from '../../../modules/brochure/components/StyledComponents/StyledComponents';
import { BooleanButtonGroup } from '../../../modules/brochure/components/PublicBooleanButtonGroup/PublicBooleanButtonGroup';
import SelectMenu from '../../components/SelectMenu/SelectMenu';
import { ADULT_TITLE_OPTIONS, CHILD_TITLE_OPTIONS } from '../../../helpers/titleOptions';

const isRequired = required({ message: 'Field is Required' });

/***
 * Styled Components
 */
const StyledInputGroup = styled(InputGroup)`
  border-radius: 4px;
  border: 1px solid;
  width: 100%;
  z-index: 0;
`;

const StyledCleave = styled(CleaveNode)`
  min-height: 45px;
  border: none;
  font-size: 14px;
  display: block;
  padding-left: 10px;
  margin: 1px;
  width: 100%;
  background: transparent;
`;

const StyledFormControl = styled.input`
  min-height: 45px;
  border: 0;
  font-size: 14px;
  display: block;
  padding-left: 10px;
  margin: 1px;
  width: 100%;
  background: transparent;
`;

const ErrorMessage = styled.span`
  color: #F8444F;
  font-size: smaller;
  line-height: 3em;
`;

const QuestionRow = styled(Row)`
  margin-bottom: 32px;
`;

const LabelSpan = styled.span`
  display: flex;
  align-items: center;
`;

const LabelText = styled(ControlLabel)`
    font-weight: normal;
    font-size: 16px;
`;

const StyledNameFields = styled.div`
  display: flex;
`;

const StyledNameInput = styled.div`
  flex-grow: 2;
  &:last-child {
    margin-right: 0;
  }
  margin-right: 10px;
`;

const TitleField = styled.div`
  min-width: 100px;
  flex: 1;
  margin-right: 10px;
`;

export const Input = ({ input, placeholder, addOnBefore, isDisabled }) => {
  return (
    <StyledInputGroup>
      {addOnBefore !== undefined && <InputGroup.Addon>{addOnBefore}</InputGroup.Addon>}
      <StyledFormControl {...input} placeholder={placeholder} disabled={isDisabled} />
    </StyledInputGroup>
  );
};

export const Cleave = ({
  input,
  placeholder,
  cleaveOptions,
  addOnBefore,
  addOnAfter,
  isDisabled,
}) => {
  const onChange = (value) => {
    input.onChange(value);
  };
  return (
    <StyledInputGroup>
      {addOnBefore !== undefined && <InputGroup.Addon>{addOnBefore}</InputGroup.Addon>}
      <StyledCleave
        {...input}
        onChange={onChange}
        options={cleaveOptions}
        placeholder={placeholder}
        disabled={isDisabled}
      />
      {addOnAfter !== undefined && <InputGroup.Addon>{addOnAfter}</InputGroup.Addon>}
    </StyledInputGroup>
  );
};

export const FormGroupInput = ({
  labelSize,
  colSize,
  label,
  meta,
  input,
  cleaveOptions,
  validate,
  addOnBefore,
  addOnAfter,
  disabled,
}) => (
  <FormGroup key={input.name}>
    {labelSize && (
      <Col sm={labelSize}>
        <LabelText>{label} </LabelText>
      </Col>
    )}
    <Col sm={colSize}>
      <ValidationState showError={meta.touched && !!meta.error}>
        {cleaveOptions ? (
          <Field
            name={input.name}
            component={Cleave}
            addOnBefore={addOnBefore}
            addOnAfter={addOnAfter}
            cleaveOptions={cleaveOptions}
            validate={validate}
            isDisabled={disabled}
          />
        ) : (
          <Field
            name={input.name}
            component={Input}
            validate={validate}
            isDisabled={disabled}
          />
        )}
        {meta.touched && !!meta.error && <ErrorMessage>{meta.error}</ErrorMessage>}
      </ValidationState>
    </Col>
  </FormGroup>
);

export const SelectMenuFormGroup = ({
  label,
  labelSize,
  colSize,
  source,
  input,
  meta,
}) => {
  const showError = meta.touched && meta.error;
  return (
    <FormGroup key={input.name}>
      <Col sm={labelSize}>
        <LabelText>{label}</LabelText>
      </Col>
      <Col sm={colSize}>
        <ValidationState showError={showError}>
          <Field
            name={input.name}
            source={source}
            component={SelectMenu}
            labelSize={labelSize}
            optionValuePath={'id'}
            label={label}
            colSize={colSize}
          />
        </ValidationState>
      </Col>
    </FormGroup>
  );
};

const ValidationState = ({ showError, children }) => {
  return <ValidationError showError={showError}>{children}</ValidationError>;
};

const Title = ({ input, meta, isChild }) => {
  const showError = meta.touched && meta.error;
  return (
    <ValidationState showError={showError}>
      <Field
        isClearable
        name={input.name}
        placeholder="Title"
        options={isChild ? CHILD_TITLE_OPTIONS : ADULT_TITLE_OPTIONS}
        component={SelectField}
      />
    </ValidationState>
  );
};

const Name = ({ input, meta, placeholder }) => {
  const showError = meta.touched && meta.error;
  return (
    <ValidationState showError={showError}>
      <Field
        name={input.name}
        placeholder={placeholder}
        normalize={capitalize}
        component={Input}
      />
      {showError && <ErrorMessage>{meta.error}</ErrorMessage>}
    </ValidationState>
  );
};
export const FormGroupBooleanButtonGroup = ({
  labelSize,
  colSize,
  label,
  input,
  meta,
  options,
}) => {
  const showError = meta.touched && meta.error;
  return (
    <QuestionRow key={input.name}>
      <Col sm={labelSize}>
        <LabelText style={{ display: 'flex' }}>
          <LabelSpan>{label}</LabelSpan>
        </LabelText>
      </Col>
      <Col sm={colSize}>
        <ValidationState showError={showError}>
          <Field
            name={input.name}
            component={BooleanButtonGroup}
            options={options}
            label={label}
          />
          {showError && meta.error && <ErrorMessage> {meta.error}</ErrorMessage>}
        </ValidationState>
      </Col>
    </QuestionRow>
  );
};

export const NameFields = ({ incTitle, input, required }) => {
  return (
    <StyledNameFields>
      {incTitle && (
        <TitleField>
          <Field
            name={input.name + '.title'}
            validate={required ? isRequired : undefined}
            component={Title}
          />
        </TitleField>
      )}
      <StyledNameInput>
        <Field
          name={input.name + '.first_name'}
          placeholder="First Name"
          validate={required ? isRequired : undefined}
          component={Name}
        />
      </StyledNameInput>
      <StyledNameInput>
        <Field
          name={input.name + '.last_name'}
          placeholder="Last Name"
          validate={required ? isRequired : undefined}
          component={Name}
        />
      </StyledNameInput>
    </StyledNameFields>
  );
};

export const FormGroupTextArea = ({ input, meta, label, props }) => {
  const showError = meta.touched && !!meta.error;

  const onChange = (value) => input.onChange(value);
  return (
    <FormGroup>
      <Col sm={4}>
        <LabelText>{label}</LabelText>
      </Col>
      <Col sm={8}>
        <ValidationState showError={showError}>
          <FormControl
            componentClass="textarea"
            rows={12}
            input={input}
            meta={meta}
            {...props}
            onChange={onChange}
          />
          {showError && <ErrorMessage>{meta.error}</ErrorMessage>}
        </ValidationState>
      </Col>
    </FormGroup>
  );
};
